import {
  BRAND_COPY,
  REFERRAL_CAP_REACHED_MESSAGE,
  REFERRAL_DISABLED_MESSAGE,
  REFERRAL_INVALID_MESSAGE,
} from '@fyf/shared';
import { useEffect, useState } from 'react';

import { checkSubmissions } from '../api/client.js';
import {
  CaptureFlowProvider,
  useCaptureDispatch,
  useCaptureState,
} from '../context/CaptureFlowContext.js';
import { ContactScreen } from '../screens/ContactScreen.js';
import { FeelingScreen } from '../screens/FeelingScreen.js';
import { GeneratingScreen } from '../screens/GeneratingScreen.js';
import { IntroScreen } from '../screens/IntroScreen.js';
import { RevealScreen } from '../screens/RevealScreen.js';
import { ShareScreen } from '../screens/ShareScreen.js';
import { SparkScreen } from '../screens/SparkScreen.js';
import { SuperpowerScreen } from '../screens/SuperpowerScreen.js';

function SurfaceTagLoader() {
  const dispatch = useCaptureDispatch();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const surface = params.get('surface');
    if (surface) {
      dispatch({ type: 'SET_SURFACE_TAG', payload: surface });
    }
    const ref = params.get('ref');
    if (ref) {
      dispatch({ type: 'SET_REFERRAL_CODE', payload: ref });
    }
  }, [dispatch]);

  return null;
}

function FlowRouter() {
  const { screen, surfaceTag, referralCode } = useCaptureState();
  const [submissionsOpen, setSubmissionsOpen] = useState<boolean | null>(null);
  const [unavailableMessage, setUnavailableMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!surfaceTag) return;

    let cancelled = false;
    checkSubmissions(surfaceTag, referralCode ?? undefined)
      .then((result) => {
        if (!cancelled) {
          setSubmissionsOpen(result.accepting);
          if (!result.accepting && result.message) {
            setUnavailableMessage(result.message);
          }
        }
      })
      .catch(() => {
        // On network error, allow the user to proceed — the POST will catch issues
        if (!cancelled) setSubmissionsOpen(true);
      });

    return () => {
      cancelled = true;
    };
  }, [surfaceTag, referralCode]);

  if (!surfaceTag) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950 px-6">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold text-white mb-4">Surface not found</h1>
          <p className="text-gray-400">
            Scan the QR code at the booth to start your flame journey.
          </p>
        </div>
      </div>
    );
  }

  if (submissionsOpen === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950 px-6">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (submissionsOpen === false) {
    const { unavailable } = BRAND_COPY.screens;
    const referralMessages: string[] = [
      REFERRAL_DISABLED_MESSAGE,
      REFERRAL_CAP_REACHED_MESSAGE,
      REFERRAL_INVALID_MESSAGE,
    ];
    const isReferralMessage = !!unavailableMessage && referralMessages.includes(unavailableMessage);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950 px-6">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold text-white mb-4">
            {isReferralMessage ? unavailableMessage : unavailable.headline}
          </h1>
          {!isReferralMessage && <p className="text-gray-400">{unavailable.subtext}</p>}
          {isReferralMessage && (
            <a
              href="https://aduro.com"
              className="mt-6 inline-block px-6 py-2 text-sm font-medium rounded-full border border-orange-700/50 bg-orange-900/30 hover:bg-orange-800/50 text-orange-300 hover:text-white transition-colors"
            >
              Learn more about Aduro
            </a>
          )}
        </div>
      </div>
    );
  }

  switch (screen) {
    case 'intro':
      return <IntroScreen />;
    case 'spark':
      return <SparkScreen />;
    case 'superpower':
      return <SuperpowerScreen />;
    case 'feeling':
      return <FeelingScreen />;
    case 'contact':
      return <ContactScreen />;
    case 'generating':
      return <GeneratingScreen />;
    case 'reveal':
      return <RevealScreen />;
    case 'share':
      return <ShareScreen />;
    default:
      return <IntroScreen />;
  }
}

export function HomePage() {
  return (
    <CaptureFlowProvider>
      <SurfaceTagLoader />
      <FlowRouter />
    </CaptureFlowProvider>
  );
}
